import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { Checkbox, Notification } from 'shared/components';
import ConfirmationPopup from 'shared/components/ConfirmationPopup';
import { licenseResetOptions } from 'shared/constants';
import {
  hasResetMaintenancePeriod,
  hasResetValidityPeriod,
  isConsumptionLicense,
} from 'shared/helpers';
import { resetLicense } from './actions';
import './styles.scss';

const ResetLicenseForm = ({ close, confirmCb, getLicense, license }) => {
  const { t } = useTranslation();
  const isMountedRef = useRef(true);
  const [isDisabled, setDisabled] = useState(false);
  const [selectedLicenseResetOptions, setSelectedLicenseResetOptions] =
    useState({ ...licenseResetOptions });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetDisabled = (value) => {
    if (isMountedRef.current) {
      setDisabled(value);
    }
  };

  const handleLicenseResetOptionsUpdate = (key, value) => {
    setSelectedLicenseResetOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const licenseProductName = get(
    license,
    'product.product_name',
    get(license, 'product.name', get(license, 'product_name', '')),
  );
  const licenseID = get(license, 'id');
  const consumptionLicense = isConsumptionLicense(license);
  const canResetValidityPeriod = hasResetValidityPeriod(license);
  const canResetMaintenancePeriod = hasResetMaintenancePeriod(license);
  const hasAdditionalResetOptions =
    canResetValidityPeriod || canResetMaintenancePeriod || consumptionLicense;

  const handleReset = () => {
    const licenseId = get(license, 'id');

    setDisabled(true);

    resetLicense(licenseId, license, selectedLicenseResetOptions)
      .then(() => {
        safeSetDisabled(false);
        getLicense();
        Notification(
          'success',
          t('Changes saved successfully'),
          t('Your license has been reset'),
        );
        confirmCb();
      })
      .catch(() => {
        safeSetDisabled(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      });
  };

  const getTitle = () => {
    if (licenseID) {
      return `${t('Reset license')} ${licenseID}`;
    }
    return `${t('Reset license')} ${licenseProductName}`;
  };

  return (
    <ConfirmationPopup
      title={getTitle()}
      closeCb={close}
      confirmCb={() => handleReset()}
      disabled={isDisabled}
      confirmText={t('Reset license')}
      theme="error"
      warning
    >
      <div className="ResetLicenseForm">
        <div className="bottom">
          {`${t('Order')}:`}
          &nbsp;
          <span>{get(license, 'order_store_id')}</span>
        </div>
        {hasAdditionalResetOptions && (
          <>
            <h4 className="reset-license-options-container-header">
              {t('You can also choose to reset the following:')}
            </h4>
            <div className="reset-license-options-container">
              <div className="reset-license-options-column">
                {canResetValidityPeriod && (
                  <Checkbox
                    label={t('Reset validity period')}
                    inputId="reset-validity-period"
                    checked={selectedLicenseResetOptions.reset_validity_period}
                    disabled={isDisabled}
                    handleChange={() =>
                      handleLicenseResetOptionsUpdate(
                        'reset_validity_period',
                        !selectedLicenseResetOptions.reset_validity_period,
                      )
                    }
                  />
                )}
                {canResetMaintenancePeriod && (
                  <Checkbox
                    label={t('Reset maintenance period')}
                    inputId="reset-maintenance-period"
                    checked={
                      selectedLicenseResetOptions.reset_maintenance_period
                    }
                    disabled={isDisabled}
                    handleChange={() =>
                      handleLicenseResetOptionsUpdate(
                        'reset_maintenance_period',
                        !selectedLicenseResetOptions.reset_maintenance_period,
                      )
                    }
                  />
                )}
                {consumptionLicense && (
                  <Checkbox
                    label={t('Reset consumptions')}
                    inputId="reset-consumptions"
                    checked={selectedLicenseResetOptions.reset_consumptions}
                    disabled={isDisabled}
                    handleChange={() =>
                      handleLicenseResetOptionsUpdate(
                        'reset_consumptions',
                        !selectedLicenseResetOptions.reset_consumptions,
                      )
                    }
                  />
                )}
              </div>
            </div>
          </>
        )}
        <h4 className="reset-license-options-label">
          {t('Core license data will always be reset.')}
        </h4>
        <h4 className="reset-license-options-label reset-license-options-label-warning">
          {t('This action cannot be undone.')}
        </h4>
      </div>
    </ConfirmationPopup>
  );
};

ResetLicenseForm.propTypes = {
  close: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  getLicense: PropTypes.func.isRequired,
  license: PropTypes.object.isRequired,
};

export default ResetLicenseForm;
